import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, MessageSquareHeart, Send } from "lucide-react";
import { toast } from "sonner";
import StudyShell from "@/components/study/StudyShell";
import { getStudentFeedback, submitStudentFeedback, trackEvent } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Choice = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "min-h-10 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors",
      active
        ? "border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.12)] text-[var(--ibp-ink)]"
        : "border-[color:rgba(10,27,51,0.16)] bg-white text-[var(--ibp-ink)] hover:border-[var(--ibp-navy-950)]",
    ].join(" ")}
  >
    {children}
  </button>
);

export default function StudyFeedback() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["student-feedback"],
    queryFn: getStudentFeedback,
  });
  const [helpful, setHelpful] = useState("");
  const [improve, setImprove] = useState("");
  const [passed, setPassed] = useState("");
  const [displayPreference, setDisplayPreference] = useState("anonymous");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await submitStudentFeedback({
        helpful,
        improve,
        passed_exam: passed === "yes" ? true : passed === "no" ? false : null,
        display_preference: displayPreference,
      });
      trackEvent("student_feedback_submitted", {
        passed_exam: passed || "not_answered",
        permission_to_quote: displayPreference !== "private",
      });
      await queryClient.invalidateQueries({ queryKey: ["student-feedback"] });
      await queryClient.invalidateQueries({ queryKey: ["study-dashboard"] });
      toast.success("Thank you for the feedback");
    } catch (error) {
      toast.error("Could not send feedback", {
        description: error?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <StudyShell testId={STUDY.feedbackPage}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[var(--ibp-navy-950)] text-[var(--ibp-gold-500)]">
          <MessageSquareHeart className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-[var(--ibp-ink)]">Share feedback</h1>
          <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
            Tell us what helped and what should improve. Your feedback goes directly to the product quality inbox.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.62)]">Loading...</p>
      ) : data?.submitted ? (
        <div className="mt-8 border-l-4 border-l-[#15803d] bg-white p-6">
          <CheckCircle2 className="h-6 w-6 text-[#15803d]" />
          <h2 className="mt-3 text-lg font-semibold text-[var(--ibp-ink)]">Feedback received</h2>
          <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
            Thank you for helping make Illinois Broker Prep clearer and more useful for the next candidate.
          </p>
          <Link
            to="/study/dashboard"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
          >
            Return to dashboard <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-7">
          <div>
            <label htmlFor="feedback-helpful" className="text-sm font-semibold text-[var(--ibp-ink)]">
              What has been most helpful?
            </label>
            <textarea
              id="feedback-helpful"
              required
              minLength={3}
              maxLength={1600}
              rows={5}
              value={helpful}
              onChange={(event) => setHelpful(event.target.value)}
              className="mt-2 w-full resize-y rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-4 py-3 text-sm leading-6 text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
              placeholder="A feature, explanation, or study moment that helped"
            />
          </div>

          <div>
            <label htmlFor="feedback-improve" className="text-sm font-semibold text-[var(--ibp-ink)]">
              What should we improve?
            </label>
            <textarea
              id="feedback-improve"
              maxLength={1600}
              rows={4}
              value={improve}
              onChange={(event) => setImprove(event.target.value)}
              className="mt-2 w-full resize-y rounded-xl border border-[color:rgba(10,27,51,0.18)] bg-white px-4 py-3 text-sm leading-6 text-[var(--ibp-ink)] outline-none focus:border-[var(--ibp-navy-950)]"
              placeholder="Optional"
            />
          </div>

          <fieldset>
            <legend className="text-sm font-semibold text-[var(--ibp-ink)]">Have you taken the exam?</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              <Choice active={passed === "yes"} onClick={() => setPassed("yes")}>Yes, I passed</Choice>
              <Choice active={passed === "no"} onClick={() => setPassed("no")}>Yes, not this time</Choice>
              <Choice active={passed === ""} onClick={() => setPassed("")}>Not yet</Choice>
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-semibold text-[var(--ibp-ink)]">Feedback privacy</legend>
            <p className="mt-1 text-xs leading-5 text-[color:rgba(11,18,32,0.58)]">
              Nothing is published automatically. An admin must review it first.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <Choice active={displayPreference === "anonymous"} onClick={() => setDisplayPreference("anonymous")}>
                Anonymous quote
              </Choice>
              <Choice active={displayPreference === "first_name"} onClick={() => setDisplayPreference("first_name")}>
                First name only
              </Choice>
              <Choice active={displayPreference === "private"} onClick={() => setDisplayPreference("private")}>
                Keep private
              </Choice>
            </div>
          </fieldset>

          <button
            type="submit"
            data-testid={STUDY.feedbackSubmit}
            disabled={busy || helpful.trim().length < 3}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {busy ? "Sending..." : "Send feedback"}
          </button>
        </form>
      )}
    </StudyShell>
  );
}
